import gql from 'graphql-tag'
import { useUploadStore } from '@/features/upload/store'
import { useUserStore } from '@/shared/stores/user'
import { useBillingStore } from '@/features/billing/store'
import { useExplorerStore } from '@/features/explorer/store'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { apolloClient, routerApolloClient } from '@/shared/graphql/client'
import {
  CreateBatchUploadUrlDocument,
  ErrorCodes,
  PlanType,
  UserPermission,
  type CreateBatchUploadUrlMutation,
  type CreateBatchUploadUrlMutationVariables,
} from '@/shared/graphql/legacy/generated'
import { getFileType } from '@/shared/utils/file'
import axios, { type AxiosProgressEvent } from 'axios'
import type { FetchResult } from '@apollo/client/core'

const UPLOAD_BATCH = 100
const RETRY_TIMEOUT_MS = 20_000

const chunk = <T>(arr: T[], size: number): T[][] => {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function useUploadFlow() {
  const uploadStore = useUploadStore()
  const userStore = useUserStore()
  const billingStore = useBillingStore()
  const explorerStore = useExplorerStore()
  const dialog = useDialog()

  const canUpload = (additionalFilesCount?: number) => {
    if (userStore.user?.showUpdatedTerms) return false

    const isPro = userStore.user?.permissions.includes(UserPermission.Pro)
    if (isPro) return true

    const currentFilesCount = userStore.user?.totalFiles || 0
    const fileLimit = userStore.user?.totalFilesLimit

    const isAtLimit = currentFilesCount === fileLimit
    const willExceedLimit =
      fileLimit && currentFilesCount + (additionalFilesCount || 0) > fileLimit

    if (isAtLimit || willExceedLimit) {
      const dialogName =
        billingStore.billing?.plan.type === PlanType.Bundle
          ? Dialogs.UpgradeBundleLimit
          : Dialogs.UpgradeToPro

      dialog.open(dialogName, {
        props: {
          willReachLimit: willExceedLimit,
          hasReachedLimit: isAtLimit,
        },
      })

      return false
    }

    return true
  }

  const showUploadDialog = () => {
    if (!canUpload()) return

    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.style.position = 'absolute'
    input.style.right = '99999px'
    document.body.append(input)

    input.click()

    input.onchange = (event: Event) => {
      document.body.removeChild(input)

      const files = (event.target as HTMLInputElement).files
      if (!files) return
      if (!canUpload(files.length)) return

      startUpload(files)
    }
  }

  const uploadFiles = (files: FileList) => {
    if (!canUpload(files.length)) return
    startUpload(files)
  }

  const startUpload = (files: FileList) => {
    const continueUpload = () => {
      dialog.open(Dialogs.ConfirmUpload, {
        props: { uploadCount: files.length },
        events: {
          success() {
            if (
              billingStore.billing?.plan.type === PlanType.Pro &&
              !userStore.user?.hasUploadedFileAsAPro
            ) {
              dialog.open(Dialogs.FirstProUpload, {
                events: {
                  success() {
                    handleFiles(files)
                  },
                },
              })
            } else {
              handleFiles(files)
              dialog.close()
            }
          },
        },
      })
    }

    if (!userStore.user?.hasUploadedFile) {
      dialog.open(Dialogs.PreUploadPrivacy, {
        events: {
          success() {
            continueUpload()
          },
        },
      })
    } else {
      continueUpload()
    }
  }

  const handleFiles = async (fileList: FileList) => {
    uploadStore.showUploadBar = true
    uploadStore.startNewSession()

    const files = Array.from(fileList)
    const newUploadIds: string[] = []

    files.forEach((file) => {
      const _id = crypto.randomUUID()
      newUploadIds.push(_id)

      uploadStore.addUpload({
        _id,
        timeLeft: 0,
        percentage: 0,
        fileName: file.name,
        abortController: new AbortController(),
        size: file.size,
      })
    })

    for (const batch of chunk(newUploadIds, UPLOAD_BATCH)) {
      const filesToUpload = files.splice(0, UPLOAD_BATCH)

      let response: FetchResult<CreateBatchUploadUrlMutation>

      const getUploadUrls = async () => {
        try {
          response = await apolloClient.mutate<
            CreateBatchUploadUrlMutation,
            CreateBatchUploadUrlMutationVariables
          >({
            mutation: CreateBatchUploadUrlDocument,
            variables: {
              input: {
                parentFolderId: explorerStore.currentFolder?.id || 'root',
                files: filesToUpload.map((file) => ({
                  fileName: file.name,
                  copyrightOwnerId: explorerStore.selectedCopyrightOwner?.id,
                  fileSize: file.size.toString(),
                })),
              },
            },
          })
        } catch (error) {
          console.error(error)

          if ((error as Error).message.includes('rate limit')) {
            await delay(RETRY_TIMEOUT_MS)
            await getUploadUrls()
          } else {
            batch.forEach((id) => {
              uploadStore.setUploadError(id, {
                error: ErrorCodes.UploadError,
              })
            })

            throw new Error('Error when uploading')
          }
        }
      }

      try {
        await getUploadUrls()
      } catch {
        continue
      }

      batch.forEach(async (id, index) => {
        const upload = uploadStore.uploads.find((u) => u._id === id)
        const uploadData = response.data!.createBatchUploadUrl.uploads[index]

        if (!upload) return

        upload.id = uploadData.headers.key.split('/')[1]

        const { __typename, ...headers } = uploadData.headers

        const formData = new FormData()

        Object.entries(headers)
          .filter(([, value]) => !!value)
          .forEach(([key, value]) => {
            formData.append(key.replace(/_/g, '-'), value!)
          })

        formData.append('file', filesToUpload[index])

        try {
          await axios.post(uploadData.url, formData, {
            signal: upload.abortController.signal,
            onUploadProgress: (event: AxiosProgressEvent) => {
              const percentage = Math.round((event.progress || 0) * 100)

              uploadStore.updateUploadProgress(upload._id, {
                percentage,
                timeLeft: Math.round(event.estimated || 0),
              })
            },
          })

          addFileToExplorerCache(upload, filesToUpload[index])
        } catch (error) {
          console.error(`[Upload] ${(error as Error).message}`)

          uploadStore.setUploadError(upload._id, {
            error: ErrorCodes.UploadError,
          })
        }
      })
    }
  }

  const addFileToExplorerCache = (
    upload: { id?: string; fileName: string },
    file: File
  ) => {
    const folderId = explorerStore.currentFolder?.id
    if (!folderId || !upload.id) return

    routerApolloClient.cache.modify({
      id: routerApolloClient.cache.identify({
        __typename: 'Folder',
        id: folderId,
      }),
      fields: {
        items(existing) {
          const newFileRef = routerApolloClient.cache.writeFragment({
            fragment: gql`
              fragment NewUploadedFile on File {
                __typename
                id
                name
                type
              }
            `,
            data: {
              __typename: 'File',
              id: upload.id,
              name: upload.fileName,
              type: getFileType(upload.fileName, file.type),
            },
          })

          if (!newFileRef) return existing

          return {
            ...existing,
            edges: [
              { __typename: 'ItemEdge', cursor: upload.id, node: newFileRef },
              ...existing.edges,
            ],
          }
        },
      },
    })
  }

  return {
    showUploadDialog,
    uploadFiles,
  }
}
